import { Editor as SlateEditor } from 'slate';
import { SectionEntry, SectionTargetLanguage, ReferenceDataEntry } from 'entities/document';
import { call, chain, flip, map, nth, pipe, prop, propEq, propIs, reduce, reject, startsWith, uniqBy, uniqWith, intersperse, descend, sort, path } from 'ramda';

/**
 * From a list of slate operations return a new list where
 * we keep only the most specific ones, i.e. the deepest paths.
 */
const mostSpecificUniqOperations = (pipe as any)(
  reject(propEq('type', 'set_selection')),
  reject(propEq('type', 'remove_node')),
  (sort as any)(descend(path(['path', 'length']) as any)),
  uniqWith((a: any, b: any) => startsWith(b.path, a.path))
);

/**
 * Returns a new function f(path) specific for the given section.
 * f will given a path return the node at path in the section.
 */
export const childFinder = (section: any) => pipe(map(nth), intersperse(prop('children') as any), reduce(flip(call), section));

/**
 * Given a list of slate operations return the changed node along with all its
 * ancestors but filter the list to only return nodes with uuid's
 */
const nodesFromOperation = (editor: SlateEditor) => (operation: any) => {
  try {
    return Array.from(
      SlateEditor.nodes(editor, {
        at: operation.path,
        match: propIs(String, 'uuid') as any,
      })
    );
  } catch {
    /**
     * Catch operation paths that does not exist anymore. For example adding new lines to a section
     * will result in them being removed from the editor but not from the operations path. This will
     * catch the out of bounds exception and return an empty set of nodes from this operation.
     */
    return [];
  }
};

/**
 * Return all nodes that should be considered changed given a list of slate operations.
 */
export const changedNodesFromOperations = (editor: SlateEditor, operations: any) =>
  (pipe as any)(
    mostSpecificUniqOperations,
    chain(nodesFromOperation(editor) as any),
    uniqBy((entry: any) => entry[0].uuid)
  )(operations);

export const navigateTo = (shadowHost: any, id: string) => {
  const elementId = `#${id}`;
  const foundElement = shadowHost.shadowRoot.querySelector(elementId);
  const y = (foundElement?.getBoundingClientRect().top ?? 0) + window.scrollY - 160;
  window.scrollTo({ top: y, behavior: 'smooth' });
};

export const findCitationWithContext = (text: string, citation: string) => {
  const trimmedCitation = citation.trim();
  if (!trimmedCitation) {
    return [];
  }
  const escapedCitation = trimmedCitation.replace(/[.*+?^${}()[\]\\|]/g, '\\$&');

  const collect = (regex: RegExp) => {
    const ranges: { start: number; end: number }[] = [];
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      ranges.push({ start: match.index, end: match.index + match[0].length });
      if (match[0].length === 0) {
        regex.lastIndex++;
      }
    }
    return ranges;
  };

  if (trimmedCitation.includes(' ')) {
    const flexibleCitation = escapedCitation.replace(/\s+/g, '\\s+');
    return collect(new RegExp(flexibleCitation, 'gi'));
  }

  const wholeWordOccurences = collect(new RegExp(`(?<![\\p{L}\\p{N}])${escapedCitation}(?![\\p{L}\\p{N}])`, 'giu'));
  return wholeWordOccurences.length > 0 ? wholeWordOccurences : collect(new RegExp(escapedCitation, 'giu'));
};

export const getClosestElementByType = (element: any, type: string): any => {
  if (!element) return null;
  if (element?.type?.toLowerCase() === type) return element;
  if (element.children) {
    for (const child of element.children) {
      return getClosestElementByType(child, type);
    }
  }
};

export const showOriginalValueReferenceTable = (pDocument: any, section: any, element: any) => {
  const documentLocale = pDocument?.locale || 'sv-SE';
  const sectionLocale =
    pDocument?.content?.sections?.flatMap((s: SectionEntry) => s.targetLanguages || []).find((tl: SectionTargetLanguage) => tl.superId === section.superId)
      ?.locale ??
    pDocument?.content?.sections?.find((s: SectionEntry) => s.superId === section.superId)?.locale ??
    documentLocale;
  const referenceDataLocale = pDocument?.content?.referenceData?.find((rd: ReferenceDataEntry) => rd.superId === element.referenceDataSuperId)?.locale;
  return referenceDataLocale === sectionLocale;
};
